import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { API_CONFIG, ApiConfig } from '../../api-config';

/**
 * Shape mínima de una opción de select. Se define localmente en lugar de
 * importar de `@ui-library` para mantener la dirección de dependencias entre
 * capas: `shared-data-access` no debe depender de libs de UI.
 *
 * El shape es compatible con `FilterOption` de `@ui-library` (mismo
 * `{ value, label }`), así que el componente puede usar el resultado
 * directo en su `[options]="..."` sin mapear.
 */
export interface ShippingMethodSelectOption {
  value: string;
  label: string;
}

/**
 * Servicio de acceso a datos para el catálogo de formas de envío expuesto
 * como opciones de select.
 *
 * A diferencia del service administrativo de settings
 * (`./settings/shipping-method.service.ts`, que apunta a
 * `/settings/shipping-methods` y devuelve la entidad completa
 * `ShippingMethod`), este service apunta al endpoint liviano de selects y
 * devuelve un shape mínimo `{ value, label }` apto para poblar
 * `<ui-select-field>` o un multi-select custom.
 *
 * Convención de endpoint (mismo patrón que el resto de `/selects/*` del
 * proyecto):
 *   GET {apiUrl}/selects/shipping-methods
 *   → { success: true, data: [{ id, text }] }
 *
 * A diferencia de los selects fiscales, este endpoint es **global** (no
 * depende del país de la empresa): si en el futuro pasa a ser
 * country-scoped, se le agrega el parámetro `countryId` y se refactoriza
 * el cache a uno anidado como en `FiscalSelectsService`.
 *
 * Cache:
 *   Singleton global (`providedIn: 'root'`) con cache en memoria indexado
 *   por URL del endpoint. Si dos componentes piden el mismo catálogo,
 *   el segundo hit es instantáneo. Si en el futuro se quiere invalidar
 *   por mutación (ej. CRUD en settings), se puede exponer un método
 *   `invalidate()` sin tocar la API pública de consumo.
 */
@Injectable({ providedIn: 'root' })
export class ShippingMethodsSelectService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  /**
   * Cache por URL de endpoint. Como el recurso es único (no se ramifica
   * por país), alcanza con un Map plano. Mutable interno, no se expone.
   */
  private readonly cache = new Map<string, ShippingMethodSelectOption[]>();

  /**
   * Endpoint único que sirve este service. Definido como constante privada
   * para que cualquier ajuste futuro (ej. agregar `?countryId=...` cuando
   * pase a ser country-scoped) se haga en un solo lugar.
   */
  private readonly endpointUrl = '/selects/shipping-methods';

  /**
   * Carga las options del catálogo de formas de envío.
   *
   * Si el endpoint ya está en cache, devuelve `of(cached)` sin hacer
   * request. Si no, hace GET y guarda en cache.
   *
   * Devuelve `Observable<ShippingMethodSelectOption[]>` que emite UNA vez
   * con el array (vacío si la API devuelve data no-array o falla). El
   * componente se subscribe y actualiza sus signals.
   *
   * Decisión: este endpoint es **global**, no recibe `countryId` (a
   * diferencia de `FiscalSelectsService.getOptionsForCountry`). Si en el
   * futuro la API requiere filtrar por país, se migra el método a la firma
   * con parámetro y se actualiza el cache a `Map<string, Map<...>>` como
   * en el service fiscal.
   */
  getOptions(): Observable<ShippingMethodSelectOption[]> {
    const cached = this.cache.get(this.endpointUrl);
    if (cached) {
      return of(cached);
    }

    return this.httpClient
      .get(`${this.apiConfig.apiUrl}${this.endpointUrl}`, {
        observe: 'response',
        responseType: 'text',
      })
      .pipe(
        map((resp) => this.parseSelectResponse(resp.body)),
        tap((options) => {
          // Guardamos incluso si es array vacío: si el endpoint devolvió
          // [] legítimo, no queremos re-fetchar cada vez.
          this.setCache(this.endpointUrl, options);
        }),
        catchError(() => {
          // Si falla, devolvemos [] silenciosamente. El componente decide
          // cómo mostrar el error (modal, banner, etc.). Acá NO guardamos
          // en cache para que un reintento futuro funcione.
          return of([]);
        }),
      );
  }

  /**
   * Parsea el body del response (que viene como `text` por la convención
   * del proyecto de hacer JSON.parse manual) y mapea a
   * `ShippingMethodSelectOption[]`.
   *
   * Convención de respuesta: `{ success: true, data: [{ id, text }] }`.
   * Si el shape no matchea, devuelve `[]` sin tirar error.
   */
  private parseSelectResponse(body: unknown): ShippingMethodSelectOption[] {
    if (typeof body !== 'string' || !body) return [];
    try {
      const parsed = JSON.parse(body) as {
        success?: boolean;
        data?: Array<{ id: string; text?: string; label?: string }>;
      };
      if (!parsed || parsed.success !== true || !Array.isArray(parsed.data)) {
        return [];
      }
      return parsed.data.map((item) => ({
        value: item.id,
        label: item.text || item.label || '',
      }));
    } catch {
      return [];
    }
  }

  private setCache(
    endpointUrl: string,
    options: ShippingMethodSelectOption[],
  ): void {
    this.cache.set(endpointUrl, options);
  }
}
